import type { VanillaLinkProps } from '@_/ui';
import type { Post } from '../../types/index.ts';
import clsx from 'clsx';
import { HiDotsHorizontal } from 'react-icons/hi';
import { PiChatCircleText, PiThumbsUp } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { buttonCommonI18n, quantityCommonI18n } from '@_/i18n-common';
import { Avatar, Button, Menu, MenuTrigger, MenuPopup, FormattedDateTime, Text } from '@_/ui';
import { TextPostGallery } from './TextPostGallery.tsx';

export interface TextPostContainerProps {
  actions?: React.ReactNode;
  authorAvatarUrl?: string | null;
  authorName: string;
  authorType: 'organization' | 'person';
  bleed?: false | 'mobile';
  children: React.ReactNode;
  onClickComment?: (event: React.MouseEvent) => void;
  onClickComments?: (event: React.MouseEvent) => void;
  onToggleLike?: () => void;
  post: Post;
  renderAuthorLink?: (props: VanillaLinkProps) => React.ReactNode;
  renderCommentLink?: (props: VanillaLinkProps) => React.ReactNode;
  renderCommentsLink?: (props: VanillaLinkProps) => React.ReactNode;
  showCommentButton?: boolean;
  showLikeButton?: boolean;
}

export function TextPostContainer({
  actions,
  authorAvatarUrl,
  authorName,
  authorType,
  bleed = false,
  children,
  onClickComment,
  onClickComments,
  onToggleLike,
  post,
  renderAuthorLink,
  renderCommentLink,
  renderCommentsLink,
  showCommentButton = true,
  showLikeButton = true
}: TextPostContainerProps) {
  const avatar = (
    <Avatar className="size-10" kind={authorType} name={authorName} src={authorAvatarUrl} />
  );

  return (
    <article
      className={clsx(
        'relative overflow-hidden',

        // Border
        'border-gray-200',
        bleed ? 'border-t border-b sm:border-r sm:border-l' : 'rounded-lg border',

        // Border radius
        bleed ? 'sm:rounded-lg' : 'rounded-lg',

        // Padding
        'pt-4 sm:mx-0 sm:px-4 sm:[--gutter-x:--spacing(4)]',
        bleed ? '-mx-(--gutter-x) px-(--gutter-x)' : 'px-4 [--gutter-x:--spacing(4)]',
        !post.images?.length &&
          !post.numLikes &&
          !post.numComments &&
          !showLikeButton &&
          !showCommentButton &&
          'pb-4'
      )}
    >
      {!!actions && (
        <div className="absolute top-4 right-5">
          <Menu>
            <MenuTrigger render={<Button icon={<HiDotsHorizontal />} variant="plain" />} />
            <MenuPopup>{actions}</MenuPopup>
          </Menu>
        </div>
      )}

      <header className="flex items-center gap-x-3">
        {/* Author avatar */}
        {renderAuthorLink ? renderAuthorLink({ children: avatar, plain: true }) : avatar}

        <div>
          {/* Author name */}
          {renderAuthorLink ? (
            renderAuthorLink({
              children: (
                <Text block semibold>
                  {authorName}
                </Text>
              ),
              plain: true
            })
          ) : (
            <Text block semibold>
              {authorName}
            </Text>
          )}

          {/* Date */}
          <Text block className="mt-0.5" size="xs" subdued>
            <FormattedDateTime relative value={post.publishedAt!} />
          </Text>
        </div>
      </header>

      {/* Content */}
      <main className="mt-4">{children}</main>

      {/* Images */}
      {!!post.images?.length && (
        <div className="-mx-(--gutter-x) mt-4">
          <TextPostGallery images={post.images} />
        </div>
      )}

      {/* Footer */}
      <div className="divide-y divide-gray-200">
        {!!(post.numLikes || post.numComments) && (
          <div className="grid grid-cols-2 gap-x-2 py-2.5">
            {/* Number of likes */}
            <div className="flex items-center">
              {!!post.numLikes && (
                <>
                  <PiThumbsUp className="text-gray-600" />
                  <Text className="ml-1.5" size="xs" subdued>
                    <FormattedMessage
                      {...quantityCommonI18n.numLikes}
                      values={{ num: post.numLikes }}
                    />
                  </Text>
                </>
              )}
            </div>

            {/* Number of comments */}
            <div className="flex items-center justify-end">
              {renderCommentsDisplay({
                numComments: post.numComments,
                onClickComments,
                renderCommentsLink
              })}
            </div>
          </div>
        )}

        {(showLikeButton || showCommentButton) && (
          <div className="flex justify-between gap-x-2 py-1.5">
            {/* Like button */}
            {showLikeButton && (
              <Button
                color={post.hasLiked ? 'primary' : 'default'}
                fillContainer
                iconLeft={<PiThumbsUp />}
                onClick={onToggleLike}
                size="sm"
                variant="subtle"
              >
                <FormattedMessage {...buttonCommonI18n.like} />
              </Button>
            )}

            {/* Comment button */}
            {showCommentButton &&
              renderCommentButton({
                onClickComment,
                renderCommentLink
              })}
          </div>
        )}
      </div>
    </article>
  );
}

function renderCommentButton({
  onClickComment,
  renderCommentLink
}: Pick<TextPostContainerProps, 'onClickComment' | 'renderCommentLink'>): React.ReactNode {
  return (
    <Button
      fillContainer
      renderLink={renderCommentLink}
      iconLeft={<PiChatCircleText />}
      onClick={onClickComment}
      size="sm"
      variant="subtle"
    >
      <FormattedMessage {...buttonCommonI18n.comment} />
    </Button>
  );
}

function renderCommentsDisplay({
  numComments,
  onClickComments,
  renderCommentsLink
}: Pick<TextPostContainerProps, 'onClickComments' | 'renderCommentsLink'> & {
  numComments: Post['numComments'];
}): React.ReactNode {
  const display = (
    <Text size="xs" subdued>
      <FormattedMessage {...quantityCommonI18n.numComments} values={{ num: numComments }} />
    </Text>
  );

  if (renderCommentsLink) {
    return renderCommentsLink({
      children: display,
      onClick: onClickComments,
      plain: true
    });
  }

  if (onClickComments) {
    return <span onClick={onClickComments}>{display}</span>;
  }

  return display;
}
